export const PERMISSION_CHANGE_EVENT = 'ops-notification-permission-change';

export const CONTINUATION_NOTIFICATION = {
  title: 'Çalışma Kontrolü',
  body: 'Çalışmaya devam ediyor musun? Süre dolmadan onay ver.',
  tag: 'ops-continuation-prompt',
} as const;

export type OpsNotificationPermission = NotificationPermission | 'unsupported';

export interface ShowOpsNotificationOptions {
  title: string;
  body: string;
  tag?: string;
  requireInteraction?: boolean;
  icon?: string;
}

const openByTag = new Map<string, Notification>();

function hasNotificationApi(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window;
}

function emitPermissionChange() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(PERMISSION_CHANGE_EVENT));
}

export function getPermission(): OpsNotificationPermission {
  if (!hasNotificationApi()) return 'unsupported';
  return Notification.permission;
}

export function canNotify(): boolean {
  return getPermission() === 'granted';
}

export async function requestPermission(): Promise<OpsNotificationPermission> {
  if (!hasNotificationApi()) {
    emitPermissionChange();
    return 'unsupported';
  }

  try {
    const permission = await Notification.requestPermission();
    emitPermissionChange();
    return permission;
  } catch {
    emitPermissionChange();
    return getPermission();
  }
}

export function showOpsNotification(options: ShowOpsNotificationOptions): Notification | null {
  if (!canNotify()) return null;

  const tag = options.tag;
  if (tag) {
    const existing = openByTag.get(tag);
    existing?.close();
    openByTag.delete(tag);
  }

  try {
    const notification = new Notification(options.title, {
      body: options.body,
      tag,
      requireInteraction: options.requireInteraction ?? false,
      icon: options.icon ?? '/favicon.svg',
    });

    notification.onclick = () => {
      try {
        window.focus();
      } catch {
        /* ignore focus failures */
      }
      notification.close();
    };

    notification.onclose = () => {
      if (tag && openByTag.get(tag) === notification) {
        openByTag.delete(tag);
      }
    };

    if (tag) openByTag.set(tag, notification);
    return notification;
  } catch {
    return null;
  }
}

export function showContinuationNotification(): Notification | null {
  return showOpsNotification({
    title: CONTINUATION_NOTIFICATION.title,
    body: CONTINUATION_NOTIFICATION.body,
    tag: CONTINUATION_NOTIFICATION.tag,
    requireInteraction: true,
  });
}

export function closeOpsNotification(tag: string): void {
  const existing = openByTag.get(tag);
  if (!existing) return;
  existing.close();
  openByTag.delete(tag);
}

export function closeContinuationNotification(): void {
  closeOpsNotification(CONTINUATION_NOTIFICATION.tag);
}

/** Listen for browser permission changes when the Permissions API supports it. */
export function bindPermissionChangeListener(): () => void {
  if (typeof window === 'undefined' || !navigator.permissions?.query) {
    return () => undefined;
  }

  let status: PermissionStatus | null = null;
  let cancelled = false;

  const onChange = () => emitPermissionChange();

  navigator.permissions
    .query({ name: 'notifications' as PermissionName })
    .then((result) => {
      if (cancelled) return;
      status = result;
      status.addEventListener('change', onChange);
    })
    .catch(() => {
      /* Permissions API may reject notifications name in some browsers */
    });

  return () => {
    cancelled = true;
    status?.removeEventListener('change', onChange);
  };
}
